import { IcoMaximize16, IcoMinimize16 } from '@onefootprint/icons';
import { AnimatePresence, motion, useSpring } from 'framer-motion';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import Button from '../button';
import TemplateEditor, { type TemplateEditorRef } from '../template-editor/template-editor';
import type { TemplateInputProps } from '../template-input/template-input';

export type TemplateTextareaProps = {
  value?: string;
  onChange?: (val: string) => void;
  height?: number;
  expandedHeight?: number;
  disabled?: boolean;
  autocompleteItems: TemplateInputProps['autocompleteItems'];
  placeholder?: string;
  size?: 'default' | 'compact';
  hasError?: boolean;
  autoFocus?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
};

export type TemplateTextareaRef = {
  focus: () => void;
};

const TemplateTextarea = forwardRef<TemplateTextareaRef, TemplateTextareaProps>(
  (
    {
      value = '',
      onChange,
      height = 150,
      expandedHeight,
      disabled = false,
      autocompleteItems,
      placeholder,
      size = 'compact',
      hasError = false,
      autoFocus,
      onFocus,
      onBlur,
    },
    ref,
  ) => {
    const editorRef = useRef<TemplateEditorRef>(null);
    const [isFocused, setIsFocused] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    // Expose focus method to parent via ref (needed for react-hook-form's setFocus)
    useImperativeHandle(ref, () => ({
      focus: () => {
        editorRef.current?.focus();
      },
    }));

    const animatedHeight = useSpring(height, {
      stiffness: 300,
      damping: 30,
    });
    const [currentHeight, setCurrentHeight] = useState(height);
    const shouldShowExpandButton = isHovered || isFocused;

    useEffect(() => {
      const targetHeight = isExpanded ? (expandedHeight ?? height * 2) : height;
      animatedHeight.set(targetHeight);
    }, [isExpanded, height, expandedHeight, animatedHeight]);

    useEffect(() => {
      const unsubscribe = animatedHeight.on('change', latest => {
        setCurrentHeight(Math.round(latest));
      });
      return unsubscribe;
    }, [animatedHeight]);

    return (
      // biome-ignore lint/a11y/noStaticElementInteractions: hover detection for expand button visibility
      <div
        className="overflow-visible w-full relative"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <AnimatePresence>
          {shouldShowExpandButton && (
            <motion.div
              className="absolute bottom-0 left-0 z-100 bg-transparent w-full p-1 rounded-b-md pointer-events-auto!"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <Button
                prefixIcon={isExpanded ? IcoMinimize16 : IcoMaximize16}
                variant="secondary"
                size="compact"
                type="button"
                onClick={() => setIsExpanded(!isExpanded)}
                className="bg-transparent! border-0! shadow-none!"
              >
                {isExpanded ? 'Collapse' : 'Expand'}
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
        <TemplateEditor
          ref={editorRef}
          height={currentHeight}
          disabled={disabled}
          placeholder={placeholder}
          size={size}
          value={value}
          onChange={onChange}
          variant="textarea"
          autocompleteItems={autocompleteItems}
          hasError={hasError}
          autoFocus={autoFocus}
          bottomPadding={48}
          onFocus={() => {
            setIsFocused(true);
            onFocus?.();
          }}
          onBlur={() => {
            setIsFocused(false);
            onBlur?.();
          }}
        />
      </div>
    );
  },
);

TemplateTextarea.displayName = 'TemplateTextarea';

export default TemplateTextarea;
